import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import AdminSidebar from "../components/AdminSidebar";
import AdminHeader from "../components/AdminHeader";
import {useGoogleMaps, Marker, GoogleMap} from './../useGoogleMaps';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  CardActions,
  Tabs,
  Tab,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import './RestaurantManagement.css';

// تهيئة مفتاح Mapbox
mapboxgl.accessToken = 'pk.eyJ1IjoibXJjY2NyZXciLCJhIjoiY204ZG1kNGx0MmVlZDJqcXU4a25xbGRibCJ9.hoeEc8I4yW_fg0ZBOWD9LA';

const MapboxAddressSearch = ({ onSelect }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);

  const searchAddresses = async (input) => {
    if (input.length < 3) return;

    setLoading(true);
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(input)}.json?` +
        `access_token=${mapboxgl.accessToken}&` +
        `country=KW&` + // تخصيص البحث للكويت
        `language=ar`
      );

      const data = await response.json();
      setSuggestions(data.features);
      setLoading(false);
    } catch (error) {
      console.error('خطأ في البحث:', error);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    searchAddresses(value);
  };

  const handleSelectAddress = (address) => {
    setQuery(address.place_name);
    onSelect({
      address: address.place_name,
      location: {
        lat: address.center[1],
        lng: address.center[0]
      }
    });
    setSuggestions([]);
  };

  return (
    <div className="address-search-container">
      <input
        type="text"
        value={query}
        onChange={handleInputChange}
        placeholder="ابحث عن العنوان في الكويت"
        className="address-input"
      />
      {loading && <div className="search-loading">جاري البحث...</div>}
      {suggestions.length > 0 && (
        <ul className="suggestions-list">
          {suggestions.map((suggestion, index) => (
            <li 
              key={index}
              onClick={() => handleSelectAddress(suggestion)}
              className="suggestion-item"
            >
              {suggestion.place_name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const RestaurantManagement = () => {
  const { t, language } = useLanguage();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRestaurant, setNewRestaurant] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    area: "",
    address: "",
    balance: 0,
    status: t('status_active'),
    location: { lat: 29.3759, lng: 47.9774 },
  });
  const [editingRestaurant, setEditingRestaurant] = useState(null);
  const [updatedData, setUpdatedData] = useState({
    name: "",
    status: "",
    balance: 0,
  });
  const [activeTab, setActiveTab] = useState('map');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  
  const kuwaitAreas = [
    t('area_capital'),
    t('area_hawally'),
    t('area_farwaniya'),
    t('area_ahmadi'),
    t('area_mubarak'),
    t('area_jahra'),
  ];
  const { isLoaded, loadError } = useGoogleMaps();

  // جلب بيانات المطاعم عند تحميل المكون
  useEffect(() => {
    fetchRestaurants();
  }, []);

  // استرجاع بيانات المطاعم من Firestore
  const fetchRestaurants = async () => {
    try {
      setLoading(true);
      console.log("📌 جاري جلب بيانات المطاعم...");
      const querySnapshot = await getDocs(collection(db, "restaurants"));

      if (querySnapshot.empty) {
        console.warn("⚠️ لا توجد بيانات متاحة في Firestore.");
        setRestaurants([]);
      } else {
        const data = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          location: doc.data().location || { lat: 29.3759, lng: 47.9774 }
        }));
        console.log("📌 البيانات المسترجعة:", data);
        setRestaurants(data);
      }
    } catch (error) {
      console.error("❌ خطأ في جلب بيانات المطاعم:", error.message);
    } finally {
      setLoading(false);
    }
  };

  // معالجة اختيار العنوان
  const handleAddressSelect = (selectedAddress) => {
    setNewRestaurant({
      ...newRestaurant,
      address: selectedAddress.address,
      location: {
        lat: selectedAddress.location.lat,
        lng: selectedAddress.location.lng
      }
    });
  };

  // إضافة مطعم جديد
  const addRestaurant = async () => {
    if (!newRestaurant.name || !newRestaurant.email || !newRestaurant.phone || !newRestaurant.password || !newRestaurant.area) {
      setError(t('fill_required_fields'));
      return;
    }

    try {
      setLoading(true);
      
      const restaurantData = {
        ...newRestaurant,
        status: t('status_active'),
        createdAt: new Date(),
        rating: 0,
        totalRatings: 0,
        reviews: [],
        categories: []
      };
      
      const docRef = await addDoc(collection(db, "restaurants"), restaurantData);
      setRestaurants([...restaurants, { id: docRef.id, ...restaurantData }]);
      setSuccess(t('restaurant_added_success'));
      
      setNewRestaurant({
        name: "",
        email: "",
        phone: "",
        password: "",
        area: "",
        address: "",
        balance: 0,
        status: t('status_active'),
        location: { lat: 29.3759, lng: 47.9774 },
      });
    } catch (error) {
      setError(t('error_adding_restaurant'));
      console.error("❌ خطأ في إضافة المطعم:", error.message);
    } finally {
      setLoading(false);
    }
  };

  // تعديل بيانات مطعم
  const startEditing = (restaurant) => {
    setEditingRestaurant(restaurant.id);
    setUpdatedData({
      name: restaurant.name || '',
      email: restaurant.email || '',
      phone: restaurant.phone || '',
      area: restaurant.area || '',
      status: restaurant.status || 'نشط',
      balance: restaurant.balance || 0,
    });
  };

  const saveChanges = async () => {
    if (!updatedData.name || !updatedData.phone) {
      alert("❌ يرجى ملء الحقول المطلوبة (الاسم ورقم الهاتف)!");
      return;
    }
    
    try {
      setLoading(true);
      
      // تحديث بيانات المطعم في قاعدة البيانات
      await updateDoc(doc(db, "restaurants", editingRestaurant), updatedData);
      
      // تحديث البيانات في الواجهة المحلية
      setRestaurants(restaurants.map(restaurant => 
        restaurant.id === editingRestaurant 
          ? { ...restaurant, ...updatedData } 
          : restaurant
      ));
      
      alert("✅ تم تعديل المطعم بنجاح!");
      setEditingRestaurant(null);
    } catch (error) {
      console.error("❌ خطأ في تعديل بيانات المطعم:", error.message);
      alert("❌ حدث خطأ أثناء تعديل بيانات المطعم: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // حذف مطعم
  const deleteRestaurant = async (restaurantId) => {
    if (window.confirm("هل أنت متأكد أنك تريد حذف هذا المطعم؟")) {
      try {
        setLoading(true);
        
        // حذف المطعم من قاعدة البيانات
        await deleteDoc(doc(db, "restaurants", restaurantId));
        
        // حذف المطعم من القائمة المحلية
        setRestaurants(restaurants.filter((r) => r.id !== restaurantId));
        
        alert("✅ تم حذف المطعم بنجاح!");
      } catch (error) {
        console.error("❌ خطأ في حذف المطعم:", error.message);
        alert("❌ حدث خطأ أثناء حذف المطعم: " + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  // تحديث الرصيد
  const updateBalance = async (restaurantId, amount) => {
    if (amount < 0) {
      alert("❌ لا يمكن أن يكون الرصيد بقيمة سالبة!");
      return;
    }
    
    try {
      setLoading(true);
      
      // تحديث الرصيد في قاعدة البيانات
      const restaurantRef = doc(db, "restaurants", restaurantId);
      await updateDoc(restaurantRef, { 
        balance: amount,
        lastBalanceUpdate: new Date()
      });
      
      // تحديث الرصيد في الواجهة المحلية
      setRestaurants((prev) =>
        prev.map((r) => (r.id === restaurantId ? { 
          ...r, 
          balance: amount,
          lastBalanceUpdate: new Date()
        } : r))
      );
      
      alert("✅ تم تحديث الرصيد بنجاح!");
    } catch (error) {
      console.error("❌ خطأ في تحديث الرصيد:", error.message);
      alert("❌ حدث خطأ أثناء تحديث الرصيد: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // تحديث حالة المطعم (نشط/غير نشط)
  const toggleRestaurantStatus = async (restaurant) => {
    try {
      setLoading(true);
      const newStatus = restaurant.status === "نشط" ? "غير نشط" : "نشط";
      
      // تحديث الحالة في قاعدة البيانات
      await updateDoc(doc(db, "restaurants", restaurant.id), { 
        status: newStatus,
        isActive: newStatus === "نشط" ? true : false,
        lastStatusUpdate: new Date()
      });
      
      // تحديث الحالة في الواجهة المحلية
      setRestaurants((prev) =>
        prev.map((r) => (r.id === restaurant.id ? { 
          ...r, 
          status: newStatus,
          isActive: newStatus === "نشط" ? true : false,
          lastStatusUpdate: new Date()
        } : r))
      );
      
      alert(`✅ تم ${newStatus === "نشط" ? "تفعيل" : "إيقاف"} المطعم بنجاح!`);
    } catch (error) {
      console.error("❌ خطأ في تحديث حالة المطعم:", error.message);
      alert("❌ حدث خطأ أثناء تحديث حالة المطعم: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // معالجة أخطاء تحميل الخريطة
  if (loadError) {
    return (
      <div>
        <AdminHeader />
        <div className="error-container">
          <h2>خطأ في تحميل الخريطة</h2>
          <p>{loadError.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="restaurant-management-layout">
      <AdminSidebar />
      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}
      
      <div className="restaurant-management-content">
        <div className="restaurant-management-header">
          <h1>{t('restaurant_management_title')}</h1>
          <div className="tab-selector">
            <button 
              className={`tab-button ${activeTab === 'map' ? 'active' : ''}`}
              onClick={() => setActiveTab('map')}
            >
              {t('restaurant_management_map')}
            </button>
            <button 
              className={`tab-button ${activeTab === 'form' ? 'active' : ''}`}
              onClick={() => setActiveTab('form')}
            >
              {t('restaurant_management_add')}
            </button>
          </div>
        </div>

        <div className="dashboard-container">
          {activeTab === 'map' ? (
            <div className="map-and-table">
              <div className="map-container">
                <div className="map-legend">
                  <div className="legend-item">
                    <div className="legend-color green"></div>
                    <span>{t('status_active')}</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color red"></div>
                    <span>{t('status_inactive')}</span>
                  </div>
                </div>
                {isLoaded ? (
                  <GoogleMap 
                    mapContainerStyle={{ width: "100%", height: "100%" }} 
                    center={{ lat: 29.3759, lng: 47.9774 }} 
                    zoom={10}
                  >
                    {restaurants.map((restaurant) => (
                      <Marker 
                        key={restaurant.id} 
                        position={restaurant.location} 
                        label={restaurant.name}
                        icon={{
                          url: restaurant.status === t('status_active') ? "/green-restaurant.png" : "/red-restaurant.png",
                        }} 
                      />
                    ))}
                  </GoogleMap>
                ) : (
                  <div className="map-loading">
                    <p>{t('restaurant_management_map_loading')}</p>
                  </div>
                )}
              </div>

              <div className="card restaurant-list-section">
                <div className="card-header">
                  <h2>{t('restaurant_management_stats')}</h2>
                  <div className="restaurant-stats">
                    <div className="stat">
                      <span className="stat-value">{restaurants.length}</span>
                      <span className="stat-label">{t('restaurant_management_total')}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-value">{restaurants.filter(r => r.status === t('status_active')).length}</span>
                      <span className="stat-label">{t('restaurant_management_active')}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-value">
                        {restaurants.reduce((sum, r) => sum + (r.balance || 0), 0).toFixed(2)}
                      </span>
                      <span className="stat-label">{t('restaurant_management_total_balance')}</span>
                    </div>
                  </div>
                </div>

                {restaurants.length === 0 ? (
                  <div className="no-data">
                    <div className="no-data-icon">🍽️</div>
                    <p>{t('restaurant_management_no_restaurants')}</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="restaurant-table">
                      <thead>
                        <tr>
                          <th>{t('restaurant_management_name')}</th>
                          <th>{t('restaurant_management_phone')}</th>
                          <th>{t('restaurant_management_status')}</th>
                          <th>{t('restaurant_management_area')}</th>
                          <th>{t('restaurant_management_balance')}</th>
                          <th>{t('restaurant_management_actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {restaurants.map((restaurant) => (
                          <React.Fragment key={restaurant.id}>
                            <tr className={editingRestaurant === restaurant.id ? 'selected-row' : ''}>
                              <td>{restaurant.name}</td>
                              <td>{restaurant.phone}</td>
                              <td>
                                <span className={`status-badge ${restaurant.status === t('status_active') ? 'active' : 'inactive'}`}>
                                  {restaurant.status}
                                </span>
                              </td>
                              <td>{restaurant.area}</td>
                              <td>
                                <div className="balance-display">
                                  <span className="balance-value">{restaurant.balance}</span>
                                  <span className="balance-currency">{t('currency')}</span>
                                </div>
                              </td>
                              <td className="actions-cell">
                                <button 
                                  className="action-button edit"
                                  onClick={() => startEditing(restaurant)}
                                >
                                  {t('restaurant_management_edit')}
                                </button>
                                {editingRestaurant === restaurant.id && (
                                  <button 
                                    className="action-button save"
                                    onClick={saveChanges}
                                  >
                                    {t('restaurant_management_save')}
                                  </button>
                                )}
                                <button 
                                  className="action-button charge"
                                  onClick={() => updateBalance(restaurant.id, restaurant.balance + 10)}
                                >
                                  {t('restaurant_management_charge')}
                                </button>
                                <button 
                                  className="action-button toggle"
                                  onClick={() => toggleRestaurantStatus(restaurant)}
                                >
                                  {restaurant.status === t('status_active') ? t('status_inactive') : t('status_active')}
                                </button>
                                <button 
                                  className="action-button delete"
                                  onClick={() => deleteRestaurant(restaurant.id)}
                                >
                                  {t('restaurant_management_delete')}
                                </button>
                              </td>
                            </tr>
                            {editingRestaurant === restaurant.id && (
                              <tr className="editing-row">
                                <td colSpan="6">
                                  <div className="editing-form">
                                    <div className="form-row">
                                      <label>{t('restaurant_management_name')}:</label>
                                      <input 
                                        type="text" 
                                        value={updatedData.name || ''} 
                                        onChange={(e) => setUpdatedData({...updatedData, name: e.target.value})}
                                      />
                                    </div>
                                    <div className="form-row">
                                      <label>{t('restaurant_management_phone')}:</label>
                                      <input 
                                        type="text" 
                                        value={updatedData.phone || ''} 
                                        onChange={(e) => setUpdatedData({...updatedData, phone: e.target.value})}
                                      />
                                    </div>
                                    <div className="form-row">
                                      <label>{t('restaurant_management_area')}:</label>
                                      <select 
                                        value={updatedData.area || ''} 
                                        onChange={(e) => setUpdatedData({...updatedData, area: e.target.value})}
                                      >
                                        <option value="">{t('select_area')}</option>
                                        {kuwaitAreas.map((area) => (
                                          <option key={area} value={area}>
                                            {area}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="add-restaurant-section">
              <div className="card">
                <div className="card-header">
                  <h2>{t('restaurant_management_add_new')}</h2>
                </div>
                <div className="card-body">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>{t('restaurant_management_restaurant_name')}</label>
                      <input 
                        type="text" 
                        placeholder={t('restaurant_management_restaurant_name')} 
                        value={newRestaurant.name} 
                        onChange={(e) => setNewRestaurant({ ...newRestaurant, name: e.target.value })}
                        className="form-control"
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('restaurant_management_email')}</label>
                      <input 
                        type="email" 
                        placeholder={t('restaurant_management_email')} 
                        value={newRestaurant.email} 
                        onChange={(e) => setNewRestaurant({ ...newRestaurant, email: e.target.value })}
                        className="form-control"
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('restaurant_management_phone_number')}</label>
                      <input 
                        type="text" 
                        placeholder={t('restaurant_management_phone_number')} 
                        value={newRestaurant.phone} 
                        onChange={(e) => setNewRestaurant({ ...newRestaurant, phone: e.target.value })}
                        className="form-control"
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('restaurant_management_password')}</label>
                      <input 
                        type="password" 
                        placeholder={t('restaurant_management_password')} 
                        value={newRestaurant.password} 
                        onChange={(e) => setNewRestaurant({ ...newRestaurant, password: e.target.value })}
                        className="form-control"
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('restaurant_management_area')}</label>
                      <select 
                        value={newRestaurant.area} 
                        onChange={(e) => setNewRestaurant({ ...newRestaurant, area: e.target.value })}
                        className="form-control"
                      >
                        <option value="">{t('select_area')}</option>
                        {kuwaitAreas.map((area) => (
                          <option key={area} value={area}>
                            {area}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>{t('restaurant_management_initial_balance')}</label>
                      <input 
                        type="number" 
                        placeholder={t('restaurant_management_initial_balance')} 
                        value={newRestaurant.balance} 
                        onChange={(e) => setNewRestaurant({ ...newRestaurant, balance: Number(e.target.value) })}
                        className="form-control"
                      />
                    </div>
                    <div className="form-group full-width">
                      <label>{t('restaurant_management_address')}</label>
                      <MapboxAddressSearch onSelect={handleAddressSelect} />
                      {newRestaurant.address && (
                        <div className="selected-address">
                          <strong>{t('restaurant_management_selected_address')}:</strong> {newRestaurant.address}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="form-actions">
                    <button 
                      className="add-button"
                      onClick={addRestaurant} 
                      disabled={loading}
                    >
                      {t('restaurant_management_add')}
                    </button>
                  </div>
                </div>
              </div>
              
              {isLoaded && (
                <div className="preview-map-container card">
                  <div className="card-header">
                    <h2>{t('restaurant_management_map_location')}</h2>
                  </div>
                  <GoogleMap 
                    mapContainerStyle={{ width: "100%", height: "300px" }} 
                    center={newRestaurant.location} 
                    zoom={15}
                  >
                    <Marker 
                      position={newRestaurant.location} 
                      icon={{
                        url: "/green-restaurant.png"
                      }} 
                    />
                  </GoogleMap>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RestaurantManagement;